/**
 * EmployeeProjectSelector Component
 *
 * Selection tool for projects, departments, and employees.
 * Ported from Vue EmployeeProjectSelector.vue
 *
 * Features:
 * - Project dropdown (multi-select)
 * - Department/team dropdown (filters employees when selected)
 * - Employee dropdown (multi-select, shown when no team is selected)
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useAtomValue } from "jotai";
import { Select } from "@/components/ui/forms/Select";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import { PointingHint } from "@/components/ui/feedback/PointingHint";
import { ProjectCard } from "@/components/ui/display/ProjectCard";
import { panelCollapsedAtom } from "@/store/bookingStore";
import { useEmployees } from "@/hooks/data/useEmployees";
import { useProjects } from "@/hooks/data/useProjects";
import { useCustomers } from "@/hooks/data/useCustomers";
import { useUser } from "@/hooks/auth/useUser";
import type { IServiceEmployee, IServiceProject } from "@/services/types";
import type { EmployeeProjectSelectorProps } from "./types";
import {
  toProjectOptionsWithData,
  toEmployeeOptionsWithData,
  toTeamOptions,
} from "@/utils/selectOptions";
import classNames from "classnames";
import styles from "./EmployeeProjectSelector.module.css";

const getEmployeeDepartment = (employee: IServiceEmployee): string => {
  const legacy = (employee as { department_name?: string | null })
    .department_name;
  return employee.department?.trim() || legacy?.trim() || "";
};

export const EmployeeProjectSelector: React.FC<
  EmployeeProjectSelectorProps
> = ({
  defaultLoggedIn = true,
  selectedEmployees = [],
  selectedProjects = [],
  horizontal = false,
  multipleEmployees = true,
  multipleProjects = true,
  employeeOnly = false,
  onSelectEmployees,
  onSelectProjects,
  className,
  size = "md",
  "data-testid": testId,
}) => {
  // Data hooks
  const {
    employees,
    bookableEmployees,
    isLoading: employeesLoading,
  } = useEmployees();
  const { activeProjects, isLoading: projectsLoading } = useProjects();
  const { customers } = useCustomers();
  const { user } = useUser();

  // Get panel collapsed state - hide hint when panel is collapsed
  const panelCollapsed = useAtomValue(panelCollapsedAtom);

  // Local state for team filter
  const [selectedTeams, setSelectedTeams] = useState<SelectOption<string>[]>(
    [],
  );

  // Track if we've done initial selection
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);

  // ============ DERIVED DATA ============

  // Get unique teams/departments from employees
  const teams = useMemo(() => {
    const teamNames = employees
      .map((e) => getEmployeeDepartment(e))
      .filter((d): d is string => Boolean(d));
    return [...new Set(teamNames)].sort();
  }, [employees]);

  // Team options for dropdown
  const teamOptions = useMemo(() => toTeamOptions(teams), [teams]);

  // Project options for dropdown
  const projectOptions = useMemo(
    () => toProjectOptionsWithData(activeProjects),
    [activeProjects],
  );

  // Employee options for dropdown (filtered by team if teams are selected)
  const employeeOptions = useMemo(() => {
    let filteredEmployees = bookableEmployees.filter((e) => !e.is_archived);

    // Filter by selected teams if any
    if (selectedTeams.length > 0) {
      const teamNames = selectedTeams.map((t) => t.label);
      filteredEmployees = filteredEmployees.filter((e) =>
        teamNames.includes(getEmployeeDepartment(e)),
      );
    }

    return toEmployeeOptionsWithData(filteredEmployees);
  }, [bookableEmployees, selectedTeams]);

  // Convert selected employees to SelectOption format
  const selectedEmployeeOptions = useMemo(
    () => toEmployeeOptionsWithData(selectedEmployees, false),
    [selectedEmployees],
  );

  // Convert selected projects to SelectOption format
  const selectedProjectOptions = useMemo(
    () => toProjectOptionsWithData(selectedProjects, false),
    [selectedProjects],
  );

  // ============ HANDLERS ============

  const handleProjectChange = useCallback(
    (selected: SelectOption<IServiceProject>[]) => {
      const projects = selected
        .map((opt) => opt.data)
        .filter((p): p is IServiceProject => p !== undefined);
      onSelectProjects?.(projects);
    },
    [onSelectProjects],
  );

  const handleTeamChange = useCallback(
    (selected: SelectOption<string>[]) => {
      setSelectedTeams(selected);

      // When teams are selected, filter employees by team and emit
      if (selected.length > 0) {
        const teamNames = selected.map((t) => t.label);
        const filteredEmps = bookableEmployees.filter(
          (e) => teamNames.includes(getEmployeeDepartment(e)) && !e.is_archived,
        );
        onSelectEmployees?.(filteredEmps);
      } else {
        // Clear employee selection when all teams are removed
        onSelectEmployees?.([]);
      }
    },
    [bookableEmployees, onSelectEmployees],
  );

  const handleEmployeeChange = useCallback(
    (selected: SelectOption<IServiceEmployee>[]) => {
      const emps = selected
        .map((opt) => opt.data)
        .filter((e): e is IServiceEmployee => e !== undefined);
      onSelectEmployees?.(emps);
    },
    [onSelectEmployees],
  );

  // ============ EFFECTS ============

  // Default selection: select logged-in user on first load
  useEffect(() => {
    if (
      !initialSelectionDone &&
      !employeesLoading &&
      employees.length > 0 &&
      defaultLoggedIn &&
      user &&
      selectedEmployees.length === 0 &&
      selectedProjects.length === 0
    ) {
      const loginEmail = user.email?.toLowerCase();
      if (loginEmail) {
        const currentUser = employees.find(
          (e) =>
            e.settings?.is_bookable && e.email?.toLowerCase() === loginEmail,
        );
        if (currentUser) {
          onSelectEmployees?.([currentUser]);
          onSelectProjects?.([]);
        }
      }
      setInitialSelectionDone(true);
    }
  }, [
    initialSelectionDone,
    employeesLoading,
    employees,
    defaultLoggedIn,
    user,
    selectedEmployees.length,
    selectedProjects.length,
    onSelectEmployees,
    onSelectProjects,
  ]);

  return (
    <div
      className={classNames(
        styles.container,
        horizontal && styles.horizontal,
        className,
      )}
      data-testid={testId}
    >
      {/* Project dropdown with pointing hint when nothing selected */}
      {!employeeOnly && (
        <PointingHint.Anchored
          direction="right"
          offset={56}
          noFadeOut
          visible={
            !panelCollapsed &&
            selectedProjects.length === 0 &&
            selectedTeams.length === 0 &&
            selectedEmployees.length === 0
          }
          data-testid="selection-hint"
          target={
            <Select<IServiceProject>
              label="Prosjekt"
              placeholder="Velg prosjekt"
              options={projectOptions}
              value={selectedProjectOptions}
              multiple={multipleProjects}
              sorted
              disabled={projectsLoading}
              onChange={handleProjectChange}
              size={size}
              renderOption={(option, state) => {
                const project = option.data;
                return (
                  <ProjectCard
                    code={project?.code ?? String(option.id)}
                    name={project?.name ?? option.label}
                    knownCustomers={customers}
                    variant="list"
                    selected={state.selected}
                    disabled={state.disabled}
                  />
                );
              }}
              data-testid="filter-project"
            />
          }
        >
          Velg prosjekt eller ansatt for å finne bookinger.
        </PointingHint.Anchored>
      )}

      {/* Department/Team dropdown */}
      <Select<string>
        label="Avdeling"
        placeholder="Velg avdeling"
        options={teamOptions}
        value={selectedTeams}
        multiple
        sorted
        disabled={employeesLoading}
        onChange={handleTeamChange}
        size={size}
        renderOption={(option, state) => (
          <div
            className={styles.listItem}
            data-selected={state.selected}
            data-disabled={state.disabled}
            style={{ textTransform: "none" }}
          >
            {option.label}
          </div>
        )}
        data-testid="filter-activity"
      />

      {/* Employee dropdown - only shown when no teams are selected */}
      {selectedTeams.length === 0 && (
        <Select<IServiceEmployee>
          label="Ansatt"
          placeholder="Velg ansatt"
          options={employeeOptions}
          value={selectedEmployeeOptions}
          multiple={multipleEmployees}
          sorted
          disabled={employeesLoading}
          onChange={handleEmployeeChange}
          size={size}
          renderOption={(option, state) => (
            <div
              className={styles.listItem}
              data-selected={state.selected}
              data-disabled={state.disabled}
              style={{ textTransform: "none" }}
            >
              {option.label}
            </div>
          )}
          data-testid="filter-employee"
        />
      )}
    </div>
  );
};

EmployeeProjectSelector.displayName = "EmployeeProjectSelector";

export default EmployeeProjectSelector;
